import type { SxProps, Theme } from "@mui/material";
import { Box } from "@mui/material";
import type { PropsWithChildren } from "react";
import { useCallback, useState } from "react";
import useMergedSx from "@entrilia/shared/hooks/useMergedSx.ts";
import type { Maybe } from "@entrilia/shared/types.ts";
import EditField from "./EditField.tsx";
import ViewField from "./ViewField.tsx";

interface Props {
  customFieldValue: Maybe<string>;
  originalFieldValue: string;
  readonly?: boolean;
  label: string;
  defaultLabel: string;
  sx?: SxProps<Theme>;
  resetToDefaultText?: string;
  validationError?: string;
  updateCustomFieldValue?: (customFieldValue: string) => void;
  onEditMode?: (isEditing: boolean) => void;
  validateInput?: (customId: string) => boolean;
  sanitizeInput?: (customId: string) => string;
}
export default function EditableField({
  customFieldValue,
  originalFieldValue,
  readonly,
  updateCustomFieldValue,
  onEditMode,
  sx,
  label,
  children,
  resetToDefaultText,
  validationError,
  defaultLabel,
  validateInput,
  sanitizeInput,
}: PropsWithChildren<Props>) {
  const [isEditingCustomLabel, setIsEditingCustomLabel] = useState(false);
  const onEditingCustomLabel = useCallback(
    (editing: boolean) => {
      setIsEditingCustomLabel(editing);
      onEditMode?.(editing);
    },
    [onEditMode]
  );

  const shouldShowDefaultLabel = !!customFieldValue && originalFieldValue !== customFieldValue;
  const mergedContainerSx = useMergedSx(
    (theme) => ({
      display: "flex",
      alignItems: "center",
      px: 2,
      pt: 2,
      pb: 1,
      height: theme.spacing(shouldShowDefaultLabel ? 6 : 5.3),
      gap: 0.5,
    }),
    sx
  );

  return (
    <Box sx={mergedContainerSx}>
      <Box sx={{ display: "flex", flex: 1, gap: 1, alignItems: "center", minWidth: 0 }}>
        {isEditingCustomLabel && updateCustomFieldValue && (
          <EditField
            customFieldValue={customFieldValue}
            originalFieldValue={originalFieldValue}
            onApply={(label) => {
              if (validateInput) {
                const isValid = validateInput(label);
                if (isValid === false) {
                  return;
                }
              }
              updateCustomFieldValue(label);
              onEditingCustomLabel(false);
            }}
            onCancel={() => onEditingCustomLabel(false)}
            resetToDefaultText={resetToDefaultText}
            validationError={validationError}
            sanitizeInput={sanitizeInput}
          />
        )}
        {!isEditingCustomLabel && (
          <ViewField
            customFieldValue={customFieldValue}
            originalFieldValue={originalFieldValue}
            onRenameClick={() => onEditingCustomLabel(true)}
            readonly={!updateCustomFieldValue || readonly}
            label={label}
            defaultLabel={defaultLabel}
            shouldShowDefaultLabel={shouldShowDefaultLabel}
          />
        )}
      </Box>
      {children}
    </Box>
  );
}
